import React from 'react'
import _ from 'underscore'
import { ChevronDown, ListTodo, LoaderCircle, RefreshCw } from 'lucide-react'
import { Button } from '@/components/ui/button'
import {
  Card,
  CardAction,
  CardContent,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { DropdownMenu } from '../dropdown_menu'
import LoadMore from '../loadmore'
import store from '../../state/store'
import TasksRow from './row'

class TasksCenter extends React.Component {

  componentDidMount() {
    // The store remembers the last-viewed slice (filters, scroll, pagination)
    // so coming back from an enquiry/lead lands the rep where they left off.
    // Only fetch on the very first visit; subsequent mounts reuse cached data.
    if (!this.props.tasks_overview.loaded) {
      store.emit('tasks_overview:index')
    }
  }

  onUserFilterChange = (value) => {
    store.emit('tasks_overview:set_user_filter', value)
  }

  onFilterChange = (value) => {
    store.emit('tasks_overview:set_filter', value)
  }

  onSearchChange = (e) => {
    const value = e.target.value
    // Update the store immediately so the input stays controlled, then debounce
    // the actual fetch.
    this.props.tasks_overview.set({ search: value })

    if (this.searchTimer) clearTimeout(this.searchTimer)
    this.searchTimer = setTimeout(() => {
      store.emit('tasks_overview:set_search', value)
    }, 250)
  }

  onLoadMore = () => {
    store.emit('tasks_overview:load_more')
  }

  onToggleTask = (task) => {
    store.emit('tasks_overview:toggle_task', task)
  }

  onTaskOwnerChange = (task, assignee) => {
    store.emit('tasks_overview:set_task_owner', task, assignee)
  }

  onTaskDueChange = (task, due_at) => {
    store.emit('tasks_overview:set_task_due', task, due_at)
  }

  onRefresh = (e) => {
    e.preventDefault()
    store.emit('tasks_overview:index', { page: 1 })
  }

  onOpenTask = (task) => {
    if (!task || !task.trollable) return

    if (task.trollable.type === 'Sales::Enquiry') {
      store.emit('enquiries:show', task.trollable.id)
    }
    else if (task.trollable.type === 'Sales::Lead') {
      store.emit('leads:show', task.trollable.id)
    }

    // Stay in the Tasks section but flip to the detail sub-view — same idea
    // as clicking a row in the pipeline grid. Clicking the rail icon resets
    // tasks_view back to 'list'.
    store.get().ui.set({ tasks_view: 'detail' })
  }

  render() {
    const tasks_overview = this.props.tasks_overview
    const userFilterOptions = tasks_overview.user_filter_options || []
    const filterOptions = tasks_overview.filter_options || []

    const user_filter_name = (_.findWhere(userFilterOptions, { value: tasks_overview.user_filter }) || {}).name
      || (tasks_overview.user_filter === 'mine' ? 'My tasks' : 'Staff member')

    const filter_name = (_.findWhere(filterOptions, { value: tasks_overview.filter }) || {}).name || 'Due today'

    const userDropdownItems = _.map(userFilterOptions, (o) => ({ value: o.value, name: o.name }))
    const filterDropdownItems = _.map(filterOptions, (o) => ({ value: o.value, name: o.name }))

    return (
      <div className="mx-auto w-full max-w-[1500px] px-6 py-6">
        <Card>
          <CardHeader className="flex flex-wrap items-center gap-2">
            <CardTitle className="flex items-center gap-2">
              <ListTodo className="size-4" />
              Tasks
            </CardTitle>
            <CardAction className="ml-auto flex flex-wrap items-center gap-2 self-center">
              <DropdownMenu
                searchable
                buttonClassName="btn btn-secondary btn-sm"
                items={userDropdownItems}
                onItemClick={this.onUserFilterChange}
              >
                {user_filter_name} <ChevronDown className="size-3.5" />
              </DropdownMenu>

              <DropdownMenu
                buttonClassName="btn btn-secondary btn-sm"
                items={filterDropdownItems}
                onItemClick={this.onFilterChange}
              >
                {filter_name} <ChevronDown className="size-3.5" />
              </DropdownMenu>

              <Input
                type="text"
                className="h-8 w-52 md:w-72"
                placeholder="Search task name"
                value={tasks_overview.search || ''}
                onChange={this.onSearchChange}
              />

              <Button
                type="button"
                variant="ghost"
                size="icon-sm"
                onClick={this.onRefresh}
                title="Refresh"
              >
                <RefreshCw className="size-4" />
              </Button>
            </CardAction>
          </CardHeader>

          <CardContent>
            <div className="hidden items-center border-b border-border pb-2 text-xs font-semibold tracking-wider text-muted-foreground uppercase md:flex">
              <div className="w-8" />
              <div className="min-w-0 flex-1">Task</div>
              <div className="w-44 shrink-0">Customer</div>
              <div className="w-32 shrink-0">Assignee</div>
              <div className="w-32 shrink-0">Due</div>
            </div>

            {tasks_overview.tasks.length === 0 && !tasks_overview.loading ? (
              <div className="py-12 text-center text-sm text-muted-foreground">
                No tasks match the current filters.
              </div>
            ) : null}

            <div className="divide-y divide-border">
              {_.map(tasks_overview.tasks, (task) => (
                <TasksRow
                  key={task.id}
                  task={task}
                  assignees={tasks_overview.assignees || []}
                  onToggle={this.onToggleTask}
                  onOpen={this.onOpenTask}
                  onOwnerChange={this.onTaskOwnerChange}
                  onDueChange={this.onTaskDueChange}
                />
              ))}
            </div>

            {tasks_overview.loading && tasks_overview.tasks.length === 0 ? (
              <div className="flex justify-center py-12">
                <LoaderCircle className="size-8 animate-spin text-muted-foreground" />
              </div>
            ) : null}

            {tasks_overview.more_results && !tasks_overview.loading ? (
              <div className="flex justify-center py-3">
                <LoadMore
                  key={`tasks-loadmore-${tasks_overview.page}`}
                  page={tasks_overview.page}
                  loading={tasks_overview.loading}
                  onLoadMore={this.onLoadMore}
                />
              </div>
            ) : null}
          </CardContent>
        </Card>
      </div>
    )
  }
}

export default TasksCenter
